import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const EMPTY = { title: "", slug: "", body: "", hero_image: "", meta_title: "", meta_description: "", author: "Siyaara Editorial", status: "draft", related_categories: [] };

export default function AdminGuides() {
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(null); // slug or "new"
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/admin/guides").then((r) => setGuides(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(load, [load]);

  const open = (g) => {
    setForm({ ...EMPTY, ...g, related_categories: g.related_categories || [] });
    setEditing(g.slug);
  };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/admin/guides", form);
      else await api.put(`/admin/guides/${editing}`, form);
      toast.success("Guide saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete guide "${slug}"?`)) return;
    await api.delete(`/admin/guides/${slug}`);
    load();
  };

  const counter = (val, max) => (
    <span className={`text-[10px] font-mono ${val.length > max ? "text-red-600" : val.length > max - 10 ? "text-amber-600" : "text-neutral-400"}`}>
      {val.length}/{max}
    </span>
  );

  if (editing !== null) {
    return (
      <div className="space-y-4 max-w-3xl" data-testid="guide-editor">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">{editing === "new" ? "New guide" : `Edit: ${editing}`}</h1>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="px-3 py-2 border rounded text-xs" data-testid="guide-cancel">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-brand text-white rounded text-xs font-medium" data-testid="guide-save">Save guide</button>
          </div>
        </div>
        <div className="bg-white border rounded p-4 space-y-3 text-sm">
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-neutral-500">Title</span>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border rounded px-2 py-1.5 w-full" data-testid="guide-title-input" /></label>
            <label className="block"><span className="text-xs text-neutral-500">Slug {editing === "new" && "(blank = auto)"}</span>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={editing !== "new"} className="border rounded px-2 py-1.5 w-full font-mono disabled:bg-neutral-50" data-testid="guide-slug-input" /></label>
            <label className="block"><span className="text-xs text-neutral-500">Meta title {counter(form.meta_title, 60)}</span>
              <input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className="border rounded px-2 py-1.5 w-full" data-testid="guide-meta-title" /></label>
            <label className="block"><span className="text-xs text-neutral-500">Meta description {counter(form.meta_description, 160)}</span>
              <input value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className="border rounded px-2 py-1.5 w-full" data-testid="guide-meta-desc" /></label>
            <label className="block"><span className="text-xs text-neutral-500">Author</span>
              <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="border rounded px-2 py-1.5 w-full" data-testid="guide-author" /></label>
            <label className="block"><span className="text-xs text-neutral-500">Hero image URL</span>
              <input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className="border rounded px-2 py-1.5 w-full" data-testid="guide-hero" /></label>
            <label className="block"><span className="text-xs text-neutral-500">Related categories (comma-sep)</span>
              <input value={form.related_categories.join(", ")} onChange={(e) => setForm({ ...form, related_categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="border rounded px-2 py-1.5 w-full" data-testid="guide-categories" /></label>
            <label className="block"><span className="text-xs text-neutral-500">Status</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border rounded px-2 py-1.5 w-full" data-testid="guide-status">
                <option value="draft">Draft (not public)</option>
                <option value="published">Published</option>
              </select></label>
          </div>
          <label className="block"><span className="text-xs text-neutral-500">Body — supports ## headings, **bold**, &gt; quotes, - lists</span>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={16} className="border rounded p-2 w-full font-mono text-xs mt-1" data-testid="guide-body-input" /></label>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="guides-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Content Hub — Guides</h1>
          <p className="text-sm text-neutral-500">Drafts are invisible on the storefront until published.</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing("new"); }} className="px-4 py-2 bg-neutral-900 text-white text-xs rounded font-medium" data-testid="new-guide-btn"><Plus size={13} className="inline mr-1" /> New guide</button>
      </div>
      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-neutral-50 text-neutral-500"><tr>{["Title", "Slug", "Status", "Author", "Updated", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody className="divide-y">
            {guides.map((g) => (
              <tr key={g.slug} data-testid={`guide-row-${g.slug}`}>
                <td className="px-3 py-2 font-medium">{g.title}</td>
                <td className="px-3 py-2 font-mono text-neutral-500">/guides/{g.slug}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded ${g.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{g.status}</span></td>
                <td className="px-3 py-2 text-neutral-500">{g.author}</td>
                <td className="px-3 py-2 text-neutral-400">{g.updated_at ? new Date(g.updated_at).toLocaleDateString("en-IN") : ""}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => open(g)} className="text-brand hover:underline mr-3" data-testid={`guide-edit-${g.slug}`}>Edit</button>
                  <button onClick={() => del(g.slug)} className="text-red-400 hover:text-red-600" data-testid={`guide-del-${g.slug}`}><Trash2 size={13} className="inline" /></button>
                </td>
              </tr>
            ))}
            {!guides.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-400">No guides yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
